// src/pages/ExternalNotifications.tsx — G-08: External Client / Authority Notification Channel
// Extends WHS notification capability to external stakeholders: TMR, Council, principal contractor.
// Acknowledgement SLA tracking. WHS Act 2011 (Qld) s.46; TMR contract obligations.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_EXTERNAL_CONTACTS, MOCK_EXTERNAL_NOTIFICATIONS, EXT_GROUP_META, ExternalContact, ExternalNotification } from '../utils/mockDataWHSMgmt'
import { MOCK_JOBS } from '../utils/mockData'
import { Send, Plus, X, CheckCircle, Clock, AlertTriangle, Phone, Mail, Users, Bell, ChevronRight } from 'lucide-react'

const fmtDateTime = (s: string) => new Date(s).toLocaleString('en-AU',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})
const elapsed = (sentAt: string): string => {
  const mins = Math.round((Date.now() - new Date(sentAt).getTime()) / 60000)
  if (mins < 60) return `${mins}m ago`
  if (mins < 1440) return `${Math.floor(mins/60)}h ${mins%60}m ago`
  return `${Math.floor(mins/1440)}d ago`
}

const EVENT_TYPE_META: Record<string, { label: string; bg: string; text: string }> = {
  INCIDENT:      { label: 'Incident notification',    bg: 'bg-red-100',    text: 'text-red-700' },
  ENVIRONMENTAL: { label: 'Environmental event',      bg: 'bg-green-100',  text: 'text-green-700' },
  PERMIT:        { label: 'Permit notification',      bg: 'bg-blue-100',   text: 'text-blue-700' },
  MANUAL:        { label: 'Manual notification',      bg: 'bg-gray-100',   text: 'text-gray-700' },
}

function NotificationStatus({ notif, sla }: { notif: ExternalNotification; sla: number }) {
  const sent = new Date(notif.sentAt)
  const elapsedMins = Math.round((Date.now() - sent.getTime()) / 60000)
  const overSla = !notif.acknowledgedAt && elapsedMins > sla

  if (notif.status === 'ACKNOWLEDGED') {
    const ackElapsed = Math.round((new Date(notif.acknowledgedAt!).getTime() - sent.getTime()) / 60000)
    return <div className="flex items-center gap-1 text-green-700 text-xs font-medium"><CheckCircle className="w-3.5 h-3.5"/>Ack {ackElapsed}m after send · {notif.acknowledgedByName}</div>
  }
  if (overSla) return <div className="flex items-center gap-1 text-red-700 text-xs font-medium"><AlertTriangle className="w-3.5 h-3.5"/>SLA exceeded — {elapsedMins}m elapsed (SLA: {sla}m)</div>
  return <div className="flex items-center gap-1 text-amber-700 text-xs font-medium"><Clock className="w-3.5 h-3.5"/>Awaiting acknowledgement · {sla - elapsedMins}m remaining</div>
}

// ── Compose notification modal ─────────────────────────────────
function ComposeModal({ contacts, onClose, onSend }: { contacts: ExternalContact[]; onClose: ()=>void; onSend: (n: ExternalNotification) => void }) {
  const [recipients, setRecipients] = useState<string[]>([])
  const [eventType, setEventType] = useState<'INCIDENT'|'ENVIRONMENTAL'|'PERMIT'|'MANUAL'>('MANUAL')
  const [message, setMessage] = useState('')
  const [severity, setSeverity] = useState('MEDIUM')
  const [eventRef, setEventRef] = useState('')

  const toggleRecipient = (id: string) => setRecipients(p => p.includes(id) ? p.filter(r => r !== id) : [...p, id])

  const handleSend = () => {
    if (!message.trim() || recipients.length === 0) return
    const notif: ExternalNotification = {
      id: `en-${Date.now()}`, sentAt: new Date().toISOString(), eventType,
      eventRef: eventRef || 'MANUAL', severity, jobNumber: 'TP-2026-001',
      recipientIds: recipients, message, status: 'SENT',
      acknowledgedAt: null, acknowledgedByName: null,
    }
    onSend(notif); onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-[#1F3864] px-6 py-4 flex items-center justify-between">
          <h3 className="font-bold text-white flex items-center gap-2"><Send className="w-4 h-4"/>Send external notification</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-white/70"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Event type</label>
              <select className="input text-sm" value={eventType} onChange={e => setEventType(e.target.value as any)}>
                {Object.entries(EVENT_TYPE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Severity</label>
              <select className="input text-sm" value={severity} onChange={e => setSeverity(e.target.value)}>
                {['CRITICAL','HIGH','MEDIUM','LOW','INFO'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Reference (incident #, permit #, etc.)</label>
              <input className="input text-sm" value={eventRef} onChange={e => setEventRef(e.target.value)} placeholder="e.g. INC-2026-003 or PTW-2026-042"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Recipients</label>
            <div className="space-y-1.5">
              {contacts.map(c => {
                const gm = EXT_GROUP_META[c.group]
                return (
                  <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border ${recipients.includes(c.id) ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input type="checkbox" checked={recipients.includes(c.id)} onChange={() => toggleRecipient(c.id)} className="accent-[#2E75B6]"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.role} · {c.organisation}</div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${gm.bg} ${gm.text} ${gm.border}`}>{gm.label}</span>
                  </label>
                )
              })}
            </div>
          </div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Message</label>
            <textarea className="input text-sm h-24 resize-none w-full" value={message} onChange={e => setMessage(e.target.value)} placeholder="Notification content — include incident reference, location, immediate actions taken, and any required response…"/>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">Notifications are sent to external parties immediately on confirmation. Ensure content is accurate before sending.</div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={handleSend} disabled={!message.trim() || recipients.length === 0} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"><Send className="w-4 h-4"/>Send notification</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ExternalNotifications() {
  const { isTier } = useAuthStore()
  const canSend = isTier('B_PC') || isTier('B_PM') || isTier('A_DEVELOPER')
  const [notifications, setNotifications] = useState<ExternalNotification[]>(MOCK_EXTERNAL_NOTIFICATIONS)
  const [contacts]                         = useState<ExternalContact[]>(MOCK_EXTERNAL_CONTACTS)
  const [showCompose, setShowCompose]       = useState(false)
  const [tab, setTab]                       = useState<'log'|'contacts'|'rules'>('log')
  const [jobFilter, setJobFilter]           = useState('ALL')

  const overdue = notifications.filter(n => n.status === 'OVERDUE').length

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Bell className="w-6 h-6 text-[#2E75B6]"/>External Notifications</h1>
          <p className="text-sm text-gray-500 mt-0.5">Client · principal contractor · authority alerts · acknowledgement SLA tracking · WHS Act 2011 s.46</p>
        </div>
        {canSend && <button onClick={() => setShowCompose(true)} className="btn-primary text-sm flex items-center gap-2"><Send className="w-4 h-4"/>Send notification</button>}
      </div>

      {overdue > 0 && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800"><AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/><span><strong>{overdue} notification{overdue !== 1 ? 's' : ''}</strong> awaiting acknowledgement past SLA deadline. Follow up with recipient directly.</span></div>}

      <div className="grid grid-cols-3 gap-3">
        {[{ label:'Sent (all)', value:notifications.length, color:'text-gray-900', bg:'bg-white border-gray-200' },{ label:'Acknowledged', value:notifications.filter(n=>n.status==='ACKNOWLEDGED').length, color:'text-green-700', bg:'bg-green-50 border-green-200' },{ label:'Overdue (past SLA)', value:overdue, color:overdue>0?'text-red-700':'text-gray-400', bg:overdue>0?'bg-red-50 border-red-200':'bg-white border-gray-200' }].map(({label,value,color,bg}) => (
          <div key={label} className={`border rounded-xl px-4 py-3 ${bg}`}><div className={`text-2xl font-bold ${color}`}>{value}</div><div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div></div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {([['log','Notification log'],['contacts','External contacts'],['rules','Auto-trigger rules']] as const).map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${tab===k?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>

      {/* Notification log */}
      {tab === 'log' && (
        <div className="space-y-3">
          {notifications.map(n => {
            const em = EVENT_TYPE_META[n.eventType]
            const recipientDetails = n.recipientIds.map(id => contacts.find(c => c.id === id)).filter(Boolean)
            const maxSlaMins = Math.min(...recipientDetails.map(c => c!.slaMins))
            const isOverdue = n.status === 'OVERDUE'
            return (
              <div key={n.id} className={`bg-white border rounded-xl p-4 ${isOverdue ? 'border-red-300 bg-red-50/20' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${em.bg} ${em.text}`}>{em.label}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${n.severity === 'HIGH' || n.severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : n.severity === 'MEDIUM' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{n.severity}</span>
                      {n.eventRef !== 'MANUAL' && <span className="text-xs font-mono text-gray-400">{n.eventRef}</span>}
                      <span className="text-xs text-gray-400">{n.jobNumber}</span>
                    </div>
                    <div className="text-sm text-gray-700">{n.message}</div>
                  </div>
                  <div className="text-xs text-gray-400 flex-shrink-0 text-right">
                    <div>{fmtDateTime(n.sentAt)}</div>
                    <div>{elapsed(n.sentAt)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex gap-1.5 flex-wrap">
                    {recipientDetails.map(c => c && (
                      <span key={c.id} className={`text-[10px] px-2 py-0.5 rounded-full border ${EXT_GROUP_META[c.group].bg} ${EXT_GROUP_META[c.group].text} ${EXT_GROUP_META[c.group].border}`}>{c.name}</span>
                    ))}
                  </div>
                  <NotificationStatus notif={n} sla={maxSlaMins} />
                </div>
              </div>
            )
          })}
          {notifications.length === 0 && <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">No external notifications sent yet</div>}
        </div>
      )}

      {/* Contact register */}
      {tab === 'contacts' && (
        <div className="space-y-3">
          <div className="flex justify-end"><button className="btn-secondary text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>Add contact</button></div>
          {(['PRINCIPAL_CONTRACTOR','CLIENT','AUTHORITY','REGULATOR'] as const).map(group => {
            const gc = contacts.filter(c => c.group === group)
            if (!gc.length) return null
            const gm = EXT_GROUP_META[group]
            return (
              <div key={group}>
                <div className={`text-xs font-bold px-3 py-1 rounded-full w-fit mb-2 ${gm.bg} ${gm.text}`}>{gm.label}</div>
                <div className="space-y-2">
                  {gc.map(c => (
                    <div key={c.id} className={`border rounded-xl p-4 ${gm.bg} ${gm.border}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-gray-900">{c.name}</div>
                          <div className="text-xs text-gray-600 mt-0.5">{c.role} · {c.organisation}</div>
                          <div className="flex gap-3 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><Phone className="w-3 h-3"/>{c.phone}</span>
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3"/>{c.email}</span>
                          </div>
                        </div>
                        <div className="text-xs space-y-1 flex-shrink-0">
                          <div className={`px-2 py-0.5 rounded text-center font-medium ${c.notifyOnIncident ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>Incidents {c.notifyOnIncident ? '✓' : '—'}</div>
                          <div className={`px-2 py-0.5 rounded text-center font-medium ${c.notifyOnEnvironmental ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>Environmental {c.notifyOnEnvironmental ? '✓' : '—'}</div>
                          <div className="text-gray-400 text-center">SLA: {c.slaMins}m</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Auto-trigger rules */}
      {tab === 'rules' && (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 font-medium">Automatic notifications are triggered when platform events meet the criteria below. Manual notifications can be sent at any time from the Notification log tab.</div>
          {[
            { trigger: 'Incident severity ≥ MEDIUM', action: 'Notify all contacts with notifyOnIncident=true for that job', timing: 'Within 15 minutes of incident submission', channel: 'Email + SMS' },
            { trigger: 'Incident severity = CRITICAL', action: 'Notify all contacts for that job + WorkSafe QLD 1300 362 128', timing: 'Immediately on submission', channel: 'Phone call required + platform notification' },
            { trigger: 'Environmental incident — EPA notifiable (spill ≥ 200L)', action: 'Notify contacts with notifyOnEnvironmental=true + DES 1300 130 372', timing: 'Within 1 hour of event submission', channel: 'Email + Phone required' },
            { trigger: 'Permit to Work rejected', action: 'Notify contacts with notifyOnPermit=true', timing: 'On rejection', channel: 'Email' },
            { trigger: 'Acknowledgement SLA exceeded', action: 'Alert WHSO via WHS Inbox (MobileComms) with recipient contact details', timing: 'On SLA breach', channel: 'Platform WHS Inbox' },
          ].map((rule, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="font-medium text-sm text-gray-900 mb-2">Trigger: <span className="text-[#2E75B6]">{rule.trigger}</span></div>
              <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                <div><div className="text-gray-400 mb-0.5">Action</div>{rule.action}</div>
                <div><div className="text-gray-400 mb-0.5">Timing</div>{rule.timing}</div>
                <div><div className="text-gray-400 mb-0.5">Channel</div>{rule.channel}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCompose && <ComposeModal contacts={contacts} onClose={() => setShowCompose(false)} onSend={n => setNotifications(p => [n, ...p])}/>}
    </div>
  )
}
